// Offline storage using localStorage (simpler alternative to IndexedDB)
// In production, use Dexie.js for better IndexedDB support

const STORAGE_KEYS = {
  PENDING_ENROLLMENTS: 'proovam_pending_enrollments',
  CACHED_DASHBOARD: 'proovam_cached_dashboard',
  CACHED_ENROLLMENTS: 'proovam_cached_enrollments',
  CACHED_WALLET: 'proovam_cached_wallet',
  LAST_SYNC: 'proovam_last_sync',
};

// ============== PENDING ENROLLMENTS (OFFLINE QUEUE) ==============

export const getPendingEnrollments = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.PENDING_ENROLLMENTS);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

export const addPendingEnrollment = (enrollment) => {
  if (enrollment?.registrationData) {
    throw new Error('Direct V2 registration requires an online connection');
  }
  const pending = getPendingEnrollments();
  const newEnrollment = {
    ...enrollment,
    id: `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    createdAt: new Date().toISOString(),
    syncStatus: 'pending',
  };
  pending.push(newEnrollment);
  localStorage.setItem(STORAGE_KEYS.PENDING_ENROLLMENTS, JSON.stringify(pending));
  return newEnrollment;
};

export const removePendingEnrollment = (id) => {
  const pending = getPendingEnrollments();
  const filtered = pending.filter((e) => e.id !== id);
  localStorage.setItem(STORAGE_KEYS.PENDING_ENROLLMENTS, JSON.stringify(filtered));
};

export const clearPendingEnrollments = () => {
  localStorage.removeItem(STORAGE_KEYS.PENDING_ENROLLMENTS);
};

// ============== CACHED DATA ==============

export const setCachedDashboard = (data) => {
  localStorage.setItem(STORAGE_KEYS.CACHED_DASHBOARD, JSON.stringify({
    data,
    timestamp: new Date().toISOString(),
  }));
};

export const getCachedDashboard = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEYS.CACHED_DASHBOARD);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

export const setCachedEnrollments = (data) => {
  localStorage.setItem(STORAGE_KEYS.CACHED_ENROLLMENTS, JSON.stringify({
    data,
    timestamp: new Date().toISOString(),
  }));
};

export const getCachedEnrollments = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEYS.CACHED_ENROLLMENTS);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

export const setCachedWallet = (data) => {
  localStorage.setItem(STORAGE_KEYS.CACHED_WALLET, JSON.stringify({
    data,
    timestamp: new Date().toISOString(),
  }));
};

export const getCachedWallet = () => {
  try {
    const cached = localStorage.getItem(STORAGE_KEYS.CACHED_WALLET);
    return cached ? JSON.parse(cached) : null;
  } catch {
    return null;
  }
};

// ============== SYNC STATUS ==============

export const setLastSync = () => {
  localStorage.setItem(STORAGE_KEYS.LAST_SYNC, new Date().toISOString());
};

export const getLastSync = () => {
  return localStorage.getItem(STORAGE_KEYS.LAST_SYNC);
};

// ============== ONLINE STATUS ==============

export const isOnline = () => navigator.onLine;

export const subscribeToOnlineStatus = (callback) => {
  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);
  
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);
  
  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};

// ============== SYNC PENDING ENROLLMENTS ==============

export const syncPendingEnrollments = async (createEnrollmentFn) => {
  if (!isOnline()) {
    console.log('[Offline] Cannot sync - device is offline');
    return { synced: 0, failed: 0 };
  }
  
  const pending = getPendingEnrollments();
  if (pending.length === 0) {
    return { synced: 0, failed: 0 };
  }
  
  console.log(`[Sync] Syncing ${pending.length} pending enrollments...`);
  
  let synced = 0;
  let failed = 0;
  
  for (const enrollment of pending) {
    if (enrollment.registrationData) {
      removePendingEnrollment(enrollment.id);
      failed++;
      continue;
    }
    try {
      await createEnrollmentFn(
        enrollment.userIdentifier || enrollment.phone,
        enrollment.productId,
        enrollment.notes,
        enrollment.campaignId,
        enrollment.userIdentifierType,
        enrollment.registrationData,
      );
      removePendingEnrollment(enrollment.id);
      synced++;
    } catch (error) {
      console.error(`[Sync] Failed to sync enrollment:`, error);
      failed++;
    }
  }
  
  setLastSync();
  console.log(`[Sync] Complete - Synced: ${synced}, Failed: ${failed}`);
  
  return { synced, failed };
};
